<script setup>

import { ElPopover } from "element-plus";
import { getRoles } from "@/composable/users/getRoles.js";
import { computed } from "vue";
import { useAuthStore } from "@/stores";

const roles = getRoles();
const authStore = useAuthStore();
const isSuperAdmin = roles?.isSuperAdmin?.value;
const isAccountManager = roles?.isAccountManager?.value;
const isSpecificManager = roles?.isSpecificManager?.value;
const isMultiStoreManager = roles?.isMultiStoreManager?.value;
const isRegionManager = roles?.isRegionManager?.value;

const hasRequiredRoles = computed(() => {
  return isAccountManager || isSuperAdmin || isSpecificManager || isMultiStoreManager || isRegionManager;
});

/** Manager dashboard: super-admin, account-manager, store manager, multi-store manager only. */
const canSeeManagerDashboard = computed(() => {
  return (
    roles.isSpecificManager.value
  );
});

const baseRoutes = [
  {
    name: "Assignment History",
    to: "/reports?tab=assignment-history",
    urlType: "lms",
  },
  {
    name: "User Progress",
    to: "/reports?tab=course-overview",
    urlType: "lms",
  },
  {
    name: "Leaderboards",
    to: "/leaderboard",
    urlType: "lms",
  }
];

const completedContentRoute = {
  name: "Completed Content Summary",
  to: "/reports?tab=completed-content-summary",
  urlType: "lms",
};

const usageReportRoute = {
  name: "Usage Reports",
  to: "/usage-report-card",
  urlType: "lms",
};

const managerDashboardRoute = computed(() => ({
  name: authStore.is_acting_as_team_leader ? "Team Leader Dashboard" : "Manager Dashboard",
  to: authStore.is_acting_as_team_leader ? "/dashboard/team-lead" : "/manager-dashboard",
  urlType: "lms",
}));

const adminDashboardRoute = computed(() => {
  let name;
  if (authStore.is_acting_as_team_leader) {
    name = "Team Leader Dashboard";
  } else if (roles.isMultiStoreManager.value || roles.isRegionManager.value) {
    name = "Manager Dashboard";
  } else {
    name = "Admin Dashboard";
  }
  return {
    name,
    to: authStore.is_acting_as_team_leader ? "/dashboard/team-lead" : "/dashboard",
    urlType: "lms",
  };
});

const getOrderedRoutes = () => {
  if (hasRequiredRoles.value) {
    // Filter out Leaderboards from baseRoutes and add it at the end
    const routesWithoutLeaderboards = baseRoutes.filter(route => route.name !== "Leaderboards");
    const leaderboardsRoute = baseRoutes.find(route => route.name === "Leaderboards");
    const extended = [
      ...routesWithoutLeaderboards,
      completedContentRoute,
      leaderboardsRoute,
      usageReportRoute,
    ];
    const hasAdminDashboardRole = roles.isSuperAdmin.value || roles.isAccountManager.value || roles.isMultiStoreManager.value || roles.isRegionManager.value;
    if (canSeeManagerDashboard.value && !hasAdminDashboardRole) {
      extended.push(managerDashboardRoute.value);
    }
    if (hasAdminDashboardRole) {
      extended.push(adminDashboardRoute.value);
    }
    return extended;
  }
  // For users without required roles, Leaderboards is already at the bottom of baseRoutes
  return baseRoutes;
};

const routes = computed(() => {
  return getOrderedRoutes();
});

</script>
<template>
  <div v-bind="$attrs">
    <el-popover :width="230" popper-style="padding: 5px !important;z-index: 3000 !important">
      <template #reference>
        <a
          class="text-untitled-gray-500 border-transparent hover:border-untitled-gray-300 border-b-2 hover:text-untitled-gray-700 px-1 pt-1 text-sm w-full h-full flex justify-center items-center font-medium"
        >
          Reports
        </a>
      </template>
      <template #default>
        <div class="w-full flex flex-col justify-start items-start">
          <div
            class="w-full"
            v-for="(route, index) in routes "
            :key="index"
          >
            <router-link
              class="w-full px-2 py-2 hover:bg-gray-200 text-sm text-untitled-gray-700 flex justify-start items-start"
              :to="route.to"
            >
              {{ route.name }}
            </router-link>
          </div>
        </div>
      </template>
    </el-popover>
  </div>
</template>
<style lang="scss" scoped></style>
